/**
 * Browser-side TripoSplat pipeline.
 *
 *  1. Preprocess: resize to 1024x1024, run RMBG (or trust user alpha), erode,
 *     crop+pad to subject, composite on black.
 *  2. Encode: DINOv3 -> (1, 4101, 1280) features; VAE encoder -> (1, 4096, 128)
 *     features (zero-padded to 4101 to match the DINOv3 prefix).
 *  3. Sample latent: flow-matching Euler loop with optional CFG, running the
 *     flow model `steps` times.
 *  4. Decode: octree sampling loop (8 levels, JS for the dynamic ops) -> point
 *     cloud -> GS decoder forward -> final Gaussian parameters.
 *  5. Render: build a .splat-format Float32 buffer and hand it to the splat
 *     viewer (mkkellogg/gaussian-splats-3d).
 */
import * as ort from "onnxruntime-web";
import type { InferenceSession, Tensor } from "onnxruntime-web";
import { type Variant } from "./components";
import { loadComponent, safeRun, getWebgpuDevice, fp16ToFp32, tensorFloat32,
         type ComponentId } from "./models";
import { GpuFlowLoop } from "./gpu-loop";

/** Quick stat summary for debugging -- call this on a Tensor or a raw typed
 *  array and you get a printable line of `min / max / mean / std`. fp16 data
 *  is decoded first so the printed numbers are meaningful, not raw uint16. */
function statsLine(label: string, src: ort.Tensor | Float32Array | Uint16Array,
                   dims?: readonly number[]): string {
  let data: Float32Array;
  let shape: readonly number[];
  let dtypeTag = "f32";
  if (src instanceof Float32Array) {
    data = src; shape = dims ?? [src.length];
  } else if (src instanceof Uint16Array) {
    data = fp16ToFp32(src); shape = dims ?? [src.length]; dtypeTag = "f16";
  } else {
    data = tensorFloat32(src); shape = src.dims;
    dtypeTag = src.type === "float16" ? "f16" : src.type === "float32" ? "f32" : src.type;
  }
  const n = data.length;
  let min = Infinity, max = -Infinity, sum = 0;
  let nans = 0, infs = 0;
  for (let i = 0; i < n; i++) {
    const v = data[i];
    if (Number.isNaN(v)) { nans++; continue; }
    if (!Number.isFinite(v)) { infs++; continue; }
    if (v < min) min = v;
    if (v > max) max = v;
    sum += v;
  }
  const finite = n - nans - infs;
  const mean = finite > 0 ? sum / finite : 0;
  let sq = 0;
  for (let i = 0; i < n; i++) {
    const v = data[i];
    if (!Number.isFinite(v)) continue;
    const d = v - mean;
    sq += d * d;
  }
  const std = finite > 0 ? Math.sqrt(sq / finite) : 0;
  const tail = (nans || infs) ? `  nan=${nans} inf=${infs}` : "";
  return `${label} dims=${JSON.stringify(shape)} ${dtypeTag} ` +
         `min=${min.toFixed(4)} max=${max.toFixed(4)} ` +
         `mean=${mean.toFixed(4)} std=${std.toFixed(4)}${tail}`;
}
import { sampleOctree } from "./octree";
import { unpackGaussians, toSplatBytes } from "./gaussians";

export interface PipelineOptions {
  steps: number;          // 10-20
  guidanceScale: number;  // 1 disables CFG
  shift: number;          // flow timestep schedule
  numGaussians: number;   // 32768..262144, multiple of 32
  selection: Record<ComponentId, Variant>;
  /** Load the un-chunked `flow_<variant>_flash.onnx` instead of the chunked
   *  baseline. Lets ORT-Web fuse SDPA -> MultiHeadAttention (flash kernel)
   *  on WebGPU. ~2x faster per step on capable adapters; requires
   *  maxStorageBufferBindingSize >~ 1 GB and the flash export to exist. */
  flowFlash?: boolean;
  /** Try the WebGPU IO-binding flow loop. Default on. Set false (UI toggle
   *  or `?gpuio=0`) to force the CPU-tensor path -- useful for confirming
   *  correctness when the IO path produces garbage. */
  flowGpuIo?: boolean;
  /** Vanilla baseline path -- match python_debug.py as closely as we can:
   *  serial encode (VAE -> then DINOv3), serial CFG per step (await cond
   *  before queueing uncond), no GPU IO binding, no flash flow. Used to
   *  isolate "did an optimization break us" vs "did the pipeline math
   *  break us". Set via `?vanilla=1`. */
  vanilla?: boolean;
  /** Pre-loaded noise tensors that override the JS-side random init in the
   *  flow sampler. When set, the flow loop starts from exactly the same
   *  noise as python_debug.py's `torch.manual_seed(0); torch.randn(...)`
   *  did, eliminating the RNG divergence from the comparison. */
  refNoise?: { latent: Float32Array; camera: Float32Array };
  /** Skip the flow sampler entirely and use this fp32 latent (shape
   *  [1, FLOW_LATENT_L, 16]) as if it had come out of the loop. Use this
   *  to test that octree + GS decoder + unpack match python_debug.py
   *  even when the flow path itself diverges (e.g. dtype slop, RNG). */
  refFlowLatent?: Float32Array;
  /** Extra bias added to opacity feature before sigmoid (see GaussianBuildOpts).
   *  Compensates for the gs_decoder fp16/int4 cast shift; ~+3 with int4
   *  brings opacity into python's range. */
  opacityBoost?: number;
  onStatus?: (msg: string) => void;
  onProgress?: (frac: number) => void;
}

const GAUSSIANS_PER_POINT = 32;

// Flow-model latent length at runtime. The model was trained at 8192. The
// browser-runnable export uses Q-chunked attention (256-row Q chunks at
// trace time), which caps the largest intermediate at chunk x L x heads x
// 4 ~= 134 MB regardless of L. So we run at full training-time L=8192 for
// no-compromise quality, with 256^2 encoder inputs (-> 261 cond tokens, to
// keep cond compute moderate). Must match the flow ONNX's traced LATENT_L.
const FLOW_LATENT_L = 8192;

export class Pipeline {
  private sessions: Partial<Record<ComponentId, InferenceSession>> = {};

  /** Lazy session creator. Called by each pipeline stage right before it
   *  needs the component, so the next stage's session.create can race
   *  against the *current* stage's work. Cached across calls; reuses the
   *  existing session if the (variant, flash) key hasn't changed. */
  private async ensureComponent(id: ComponentId, opts: PipelineOptions)
                                : Promise<InferenceSession> {
    const v = opts.selection[id];
    const flash = id === "flow" && !!opts.flowFlash;
    const key = `${v}${flash ? "+flash" : ""}`;
    if (!this.sessions[id] || (this.sessions[id] as any)._variantKey !== key) {
      opts.onStatus?.(`loading ${id} (${key})`);
      const sess = await loadComponent(id, v, flash ? { flash: true } : undefined);
      (sess as any)._variantKey = key;
      this.sessions[id] = sess;
    }
    return this.sessions[id]!;
  }

  /** Eager pre-warm. Optional -- kept so callers that want everything
   *  loaded before the first run (e.g. for a stable timing measurement)
   *  can opt in. The default `run()` now lazy-loads each session at the
   *  start of its stage. */
  async ensureLoaded(opts: PipelineOptions) {
    const ids: ComponentId[] = ["vae_encoder", "dinov3", "flow", "octree_step", "gs_decoder"];
    for (const id of ids) await this.ensureComponent(id, opts);
  }

  async run(rgbMinus1to1: Float32Array, rgbImagenet: Float32Array,
            opts: PipelineOptions): Promise<ArrayBuffer> {
    // Reset the WebGPU fatal-error flag so a previous error doesn't block
    // subsequent attempts. If the device is genuinely lost (not just an
    // op-level validation error) ORT will surface that separately.
    (globalThis as any).__webgpuFatalError = undefined;

    opts.onStatus?.("encoding image");
    const tEncode0 = performance.now();
    const cond = await this.encode(rgbMinus1to1, rgbImagenet, opts);
    const tEncode = performance.now() - tEncode0;

    opts.onStatus?.("sampling latent");
    const tFlow0 = performance.now();
    const latent = await this.sampleLatent(cond, opts);
    const tFlow = performance.now() - tFlow0;

    opts.onStatus?.("decoding gaussians");
    const tDecode0 = performance.now();
    const splat = await this.decodeGaussians(latent, opts);
    const tDecode = performance.now() - tDecode0;

    const total = tEncode + tFlow + tDecode;
    console.log(`[timing] encode=${tEncode.toFixed(0)}ms  ` +
                `flow=${tFlow.toFixed(0)}ms (${(tFlow/opts.steps).toFixed(0)}ms/step)  ` +
                `decode=${tDecode.toFixed(0)}ms  ` +
                `TOTAL=${total.toFixed(0)}ms`);
    return splat;
  }

  // -------------------------------------------------------------------------

  private async encode(rgbMinus1to1: Float32Array, rgbImagenet: Float32Array,
                       opts: PipelineOptions)
                       : Promise<{ feat1: Tensor; feat2: Tensor }> {
    console.log("[pipeline]", statsLine("rgbMinus1to1", rgbMinus1to1, [1, 3, 1024, 1024]));
    console.log("[pipeline]", statsLine("rgbImagenet ", rgbImagenet,  [1, 3, 1024, 1024]));

    // Lazy-load each encoder right before it's needed -- dinov3 (~440 MB)
    // is the biggest of the two so we want to overlap its session.create
    // with the rgbImagenet preprocess running on the main thread.
    //
    // Always serial. ORT-Web's WASM backend is single-threaded (multi-
    // threading needs crossOriginIsolated, which the deployment doesn't
    // set), so two concurrent `session.run` calls crash with "Session
    // already started" -- even across different sessions. Matches
    // python_debug.py's order (dinov3 -> vae) too.
    const dino = await this.ensureComponent("dinov3", opts);
    const dinoOut = await safeRun(dino, {
      pixel_values: new ort.Tensor("float32", rgbImagenet, [1, 3, 1024, 1024]),
    });
    const feat1 = dinoOut.features as Tensor;
    const vae = await this.ensureComponent("vae_encoder", opts);
    const vaeOut = await safeRun(vae, {
      images: new ort.Tensor("float32", rgbMinus1to1, [1, 3, 1024, 1024]),
    });
    const feat2 = vaeOut.latent as Tensor;
    console.log("[pipeline]", statsLine("vae feat2   ", feat2));
    console.log("[pipeline]", statsLine("dino feat1  ", feat1));

    // Pad feat2's prefix with zeros so its 4096 tokens align with feat1's
    // patch tokens (positions 5..4100).
    const feat2Padded = padPrefix(feat2, 5);
    console.log("[pipeline]", statsLine("feat2Padded ", feat2Padded));
    return { feat1, feat2: feat2Padded };
  }

  private async sampleLatent(cond: { feat1: Tensor; feat2: Tensor },
                             opts: PipelineOptions): Promise<Tensor> {
    // `?skipflow=1`: don't run the flow loop at all -- treat python's
    // saved latent as the result. The rest of the pipeline then has the
    // same starting point as python_debug.py did after its 20 Euler
    // steps, so any divergence localizes to decode/unpack. No flow
    // session needed in this branch.
    if (opts.refFlowLatent) {
      const expected = 1 * FLOW_LATENT_L * 16;
      if (opts.refFlowLatent.length !== expected) {
        throw new Error(`refFlowLatent length ${opts.refFlowLatent.length} != ${expected}`);
      }
      console.log("[pipeline] flow loop: skipped, using reference flow_latent");
      const t = new ort.Tensor("float32", opts.refFlowLatent, [1, FLOW_LATENT_L, 16]);
      console.log("[pipeline]", statsLine("flow latent ", t));
      return t;
    }
    const flow = await this.ensureComponent("flow", opts);
    const cfgOn = opts.guidanceScale > 1;
    const expectedLatentN = 1 * FLOW_LATENT_L * 16;
    const expectedCameraN = 1 * 1 * 5;
    let initLatent: Float32Array, initCamera: Float32Array;
    if (opts.refNoise) {
      if (opts.refNoise.latent.length !== expectedLatentN) {
        throw new Error(`refNoise.latent length ${opts.refNoise.latent.length} ` +
                        `!= expected ${expectedLatentN}`);
      }
      if (opts.refNoise.camera.length !== expectedCameraN) {
        throw new Error(`refNoise.camera length ${opts.refNoise.camera.length} ` +
                        `!= expected ${expectedCameraN}`);
      }
      initLatent = opts.refNoise.latent;
      initCamera = opts.refNoise.camera;
      console.log("[pipeline] flow init: using reference noise (matches python torch.manual_seed(0))");
    } else {
      initLatent = randnFloat32(expectedLatentN);
      initCamera = randnFloat32(expectedCameraN);
    }

    // Fast path: WebGPU IO binding keeps latent + camera on the GPU and runs
    // the Euler+CFG update in a WGSL kernel. ~40 saved round-trips with CFG.
    // Opt-out via the UI toggle or `?gpuio=0`. Vanilla mode always uses CPU.
    // No silent fallback -- errors from sampleLatentGpu propagate so we
    // notice them instead of running a slow CPU path for free.
    const device = getWebgpuDevice();
    const gpuIoOn = !opts.vanilla && opts.flowGpuIo !== false;
    if (device && gpuIoOn) {
      return await this.sampleLatentGpu(flow, cond, opts, device,
                                        initLatent, initCamera);
    }
    if (!gpuIoOn) {
      console.log("[pipeline] flow loop: using CPU-tensor path (GPU IO disabled)");
    }

    // CPU-tensor fallback. Same math, but every step ferries latent + camera
    // between CPU and GPU.
    let latent: Tensor = new ort.Tensor("float32", initLatent, [1, FLOW_LATENT_L, 16]);
    let camera: Tensor = new ort.Tensor("float32", initCamera, [1, 1, 5]);
    const negFeat1 = zerosLike(cond.feat1);
    const negFeat2 = zerosLike(cond.feat2);

    const tSeq = flowTimesteps(opts.steps, opts.shift);
    console.log(`[pipeline] CPU flow loop: steps=${opts.steps} cfg=${opts.guidanceScale} ` +
                `shift=${opts.shift} flow.input.latent dtype=${tensorInputType(flow, "latent")}`);
    for (let i = 0; i < opts.steps; i++) {
      const t = tSeq[i], tPrev = tSeq[i + 1];
      const dt = t - tPrev;
      const tTensor = new ort.Tensor("float32", new Float32Array([1000 * t]), [1]);

      // Serial CFG (same reason as encode: ORT-Web single-thread can't
      // overlap session.run calls). cond then uncond then blend.
      const condOut = await safeRun(flow, {
        latent, camera, t: tTensor, feat1: cond.feat1, feat2: cond.feat2,
      });
      let uncondOut: any = null;
      if (cfgOn) {
        uncondOut = await safeRun(flow, {
          latent, camera, t: tTensor, feat1: negFeat1, feat2: negFeat2,
        });
      }
      let vLatent = condOut.latent_out as Tensor;
      let vCamera = condOut.camera_out as Tensor;
      if (uncondOut) {
        vLatent = cfgBlend(vLatent, uncondOut.latent_out as Tensor, opts.guidanceScale);
        vCamera = cfgBlend(vCamera, uncondOut.camera_out as Tensor, opts.guidanceScale);
      }

      latent = stepEuler(latent, vLatent, dt);
      camera = stepEuler(camera, vCamera, dt);

      // Per-step trace: cheap (one stats pass over 131k floats), and the only
      // way to see the loop diverge to NaN or explode in real time.
      if (i === 0 || i === opts.steps - 1 || i % 5 === 0) {
        console.log(`[pipeline] step ${i+1}/${opts.steps} t=${t.toFixed(3)} dt=${dt.toFixed(3)}`);
        console.log("[pipeline]   ", statsLine("vLatent", vLatent));
        console.log("[pipeline]   ", statsLine("latent ", latent));
      }
      opts.onProgress?.((i + 1) / opts.steps);
    }
    console.log("[pipeline]", statsLine("flow latent ", latent));
    return latent;
  }

  private async sampleLatentGpu(flow: InferenceSession,
                                cond: { feat1: Tensor; feat2: Tensor },
                                opts: PipelineOptions,
                                device: GPUDevice,
                                initLatent: Float32Array,
                                initCamera: Float32Array): Promise<Tensor> {
    const cfgOn = opts.guidanceScale > 1;
    const condL = cond.feat1.dims[1];
    const feat1Ch = cond.feat1.dims[2];
    const feat2Ch = cond.feat2.dims[2];
    // The encoders' outputs may be fp16 (default vae_encoder=fp16,
    // dinov3=int4 -> fp16 IO). Decode to fp32 before handing to the GPU
    // loop -- it'll re-encode to whichever dtype the flow model wants.
    const feat1F32 = tensorFloat32(cond.feat1);
    const feat2F32 = tensorFloat32(cond.feat2);

    const flowDtype = tensorInputType(flow, "latent");
    console.log(`[pipeline] GPU IO flow loop: steps=${opts.steps} cfg=${opts.guidanceScale} ` +
                `shift=${opts.shift} flow.input.latent=${flowDtype} ` +
                `condL=${condL} feat1Ch=${feat1Ch} feat2Ch=${feat2Ch}`);
    console.log("[pipeline]", statsLine("feat1 (cpu->gpu)", feat1F32, cond.feat1.dims));
    console.log("[pipeline]", statsLine("feat2 (cpu->gpu)", feat2F32, cond.feat2.dims));

    const loop = new GpuFlowLoop();
    await loop.init({
      device, flow, L: FLOW_LATENT_L, condL, feat1Ch, feat2Ch,
      feat1: feat1F32, feat2: feat2F32,
      initLatent, initCamera, useCfg: cfgOn,
    });
    try {
      const tSeq = flowTimesteps(opts.steps, opts.shift);
      for (let i = 0; i < opts.steps; i++) {
        const t = tSeq[i], tPrev = tSeq[i + 1];
        await loop.step(t, t - tPrev, opts.guidanceScale, cfgOn);
        // No mid-loop peek -- readback forces a GPU sync + 512 KB copy
        // + JS stats pass that's the entire point of GPU IO to avoid.
        // The one-shot verifyOrtHonoredFetches in step() prints a
        // sanity check on step 0; the final latent prints below.
        opts.onProgress?.((i + 1) / opts.steps);
      }
      const latent = await loop.finalizeLatent();
      console.log("[pipeline]", statsLine("flow latent ", latent));
      return latent;
    } finally {
      loop.destroy();
    }
  }

  private async decodeGaussians(latent: Tensor, opts: PipelineOptions): Promise<ArrayBuffer> {
    // octree first -- sampleOctree needs it before we touch gs_decoder.
    const octree = await this.ensureComponent("octree_step", opts);
    const gs     = await this.ensureComponent("gs_decoder", opts);
    const numPoints = Math.max(1, Math.floor(opts.numGaussians / GAUSSIANS_PER_POINT));
    console.log(`[pipeline] decode: numPoints=${numPoints} ` +
                `octree.cond=${tensorInputType(octree, "cond")} ` +
                `gs.cond=${tensorInputType(gs, "cond")}`);

    // a) JS octree loop -> point cloud (1, L, 3) where L = numPoints.
    const points = await sampleOctree(octree, latent, numPoints);
    console.log("[pipeline]", statsLine("octree pts  ", points, [1, numPoints, 3]));

    // b) GS decoder forward -- (1, L, 480) features.
    const pTensor = new ort.Tensor("float32", points, [1, numPoints, 3]);
    const gsOut = await safeRun(gs, { points: pTensor, cond: latent });
    const features = gsOut.features as Tensor;
    console.log("[pipeline]", statsLine("gs features ", features));

    // c) Unpack features into per-gaussian primitives, sort by salience,
    //    serialize to the 32-byte .splat record.
    const unpacked = unpackGaussians({ points, features, opacityBoost: opts.opacityBoost });
    // Sanity check the unpacked primitives. If a buggy upstream sent us
    // garbage (e.g. an fp16 reinterp bug) opacity collapses to a single
    // value and the visible gaussian count drops to ~0.
    const opStats = quickStats(unpacked.op);
    const visible = countAbove(unpacked.op, 0.01);
    console.log(`[pipeline] gaussians: total=${unpacked.op.length}  ` +
                `visible(op>0.01)=${visible}  ` +
                `opacity min=${opStats.min.toFixed(3)} max=${opStats.max.toFixed(3)} ` +
                `mean=${opStats.mean.toFixed(3)}`);
    const xyzStats = quickStats(unpacked.xyz);
    const scStats  = quickStats(unpacked.scale);
    console.log(`[pipeline] xyz   min=${xyzStats.min.toFixed(3)} max=${xyzStats.max.toFixed(3)} ` +
                `mean=${xyzStats.mean.toFixed(3)}`);
    console.log(`[pipeline] scale min=${scStats.min.toFixed(4)} max=${scStats.max.toFixed(4)} ` +
                `mean=${scStats.mean.toFixed(4)}`);
    const splat = toSplatBytes(unpacked);
    console.log(`[pipeline] splat bytes=${splat.byteLength} ` +
                `(${unpacked.op.length} gaussians x 32 bytes)`);
    return splat;
  }
}

function tensorInputType(sess: InferenceSession, name: string): string {
  const m = sess.inputMetadata.find(m => m.name === name);
  if (m && m.isTensor) return (m as ort.InferenceSession.TensorValueMetadata).type;
  return "?";
}

function quickStats(a: Float32Array) {
  let min = Infinity, max = -Infinity, sum = 0;
  for (let i = 0; i < a.length; i++) {
    const v = a[i];
    if (!Number.isFinite(v)) continue;
    if (v < min) min = v;
    if (v > max) max = v;
    sum += v;
  }
  return { min, max, mean: a.length ? sum / a.length : 0 };
}
function countAbove(a: Float32Array, threshold: number): number {
  let n = 0;
  for (let i = 0; i < a.length; i++) if (a[i] > threshold) n++;
  return n;
}

// ---------------------------------------------------------------------------
// helpers
// ---------------------------------------------------------------------------

function randnFloat32(n: number): Float32Array {
  const data = new Float32Array(n);
  for (let i = 0; i < n; i += 2) {
    const u1 = Math.max(Math.random(), 1e-9), u2 = Math.random();
    const r = Math.sqrt(-2 * Math.log(u1));
    data[i] = r * Math.cos(2 * Math.PI * u2);
    if (i + 1 < n) data[i + 1] = r * Math.sin(2 * Math.PI * u2);
  }
  return data;
}

function zerosLike(t: Tensor): Tensor {
  // Always fp32 -- safeRun.adaptInput will downcast on the way into a fp16
  // session. Sized by element count regardless of input dtype.
  const elems = t.dims.reduce((a, b) => a * b, 1);
  return new ort.Tensor("float32", new Float32Array(elems), t.dims);
}

function padPrefix(t: Tensor, n: number): Tensor {
  const [B, L, C] = t.dims;
  const out = new Float32Array(B * (L + n) * C);
  // Decode fp16 if needed -- the VAE encoder runs in fp16 by default, so
  // tensor.data here is Uint16Array (fp16 bit patterns) more often than not.
  const srcFull = tensorFloat32(t);
  for (let b = 0; b < B; b++) {
    out.set(srcFull.subarray(b * L * C, (b + 1) * L * C),
            b * (L + n) * C + n * C);
  }
  return new ort.Tensor("float32", out, [B, L + n, C]);
}

function flowTimesteps(steps: number, shift: number): number[] {
  const t: number[] = [];
  for (let i = 0; i <= steps; i++) {
    const u = 1 - i / steps;
    t.push((shift * u) / (1 + (shift - 1) * u));
  }
  return t;
}

function cfgBlend(condV: Tensor, uncondV: Tensor, s: number): Tensor {
  // tensorFloat32 decodes fp16 outputs. The flow model is fp16 in the
  // default int4 variant -- without this, both arrays would be Uint16 bit
  // patterns reinterpreted as fp32 noise, and the sampled latent would
  // diverge to garbage within a few steps.
  const a = tensorFloat32(condV);
  const b = tensorFloat32(uncondV);
  const out = new Float32Array(a.length);
  for (let i = 0; i < out.length; i++) out[i] = s * a[i] - (s - 1) * b[i];
  return new ort.Tensor("float32", out, condV.dims);
}

function stepEuler(x: Tensor, v: Tensor, dt: number): Tensor {
  const xs = tensorFloat32(x);
  const vs = tensorFloat32(v);
  const out = new Float32Array(xs.length);
  for (let i = 0; i < out.length; i++) out[i] = xs[i] - vs[i] * dt;
  return new ort.Tensor("float32", out, x.dims);
}
